import {
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ERROR_ADD_PENNY_PRODUCT_MESSAGE, SPECIAL_METHOD } from '@/constants';
import classNames from 'classnames';
import { cloneDeep, find, findIndex } from 'lodash';

import { useAnalytics, useEcomStoreSelector, useUiStore } from '@/data';
import { Button, IconSvg, Select, useData } from '@/components';
import { useBundleSpecial, useNotify, useProducts } from '@/hooks';
import { CartLineItem, GProduct, Variant } from '@/types';
import { currencyFormat, getProductItemLocId } from '@/utils';

import CounterTestIds from './CounterTestIds';
import { buildBogoClassifyParams } from '../../hooks/useBundleSpecial/bogoClassify';
import {
  isBogoGetAdd,
  resolvePreCartSpecialContext,
} from '../../utils/bogoSpecialCart';
import {
  notifyCatalogBogoAdd,
  notifyNonBogoBundleAdd,
  notifyRegularProductAdd,
  resolveShouldOpenCartOnAdd,
  runBogoDealPageAddNotify,
} from './counterDealNotify';

import {
  // calculateTotalGrams,
  canAddSpecialProductToCart,
  formatWeightLabel,
  sortVariantByPrice,
} from './Utils';
import { ISelectOption } from '../Select/Select';

import style from './counter.module.scss';
import { CounterActions } from './CounterActions';
import { useProductContext } from '../Product/context';

/**
 * The attr listed below are attr added to the ecom products
 * to be able to implement the cart funcionality
 * - item_id
 * - quantity
 * - weightVariant
 */

interface ICounterProps {
  product: CartLineItem | GProduct;
  onCard?: boolean;
  onDetail?: boolean;
  onShopping?: boolean;
  onKiosk?: boolean;
  showIcon?: boolean;
  omitCheckAvilibility?: boolean;
  actionTheme?: 'primary' | 'secondary' | 'tertiary' | 'link';
  selectedWeightVariant?: Variant;
  /* callbackIfBundle?: (
    _bundleSpecial: JaneSpecial,
    _bundleProducts: GProduct[],
  ) => void; */
  //handleBundlePopup?: (_id: string) => void;
  onKioskAddClicked?: () => void;
  renderWeightVariant?: ReactNode;
  renderPriceFavAction?: ReactNode;
  renderContinueShopping?: ReactNode;
  className?: string;
  classNameCounter?: string;
}

const Counter: FC<ICounterProps> = ({
  product,
  onCard,
  onDetail,
  onShopping,
  onKiosk,
  showIcon,
  omitCheckAvilibility,
  selectedWeightVariant,
  actionTheme = 'link',
  // callbackIfBundle,
  // handleBundlePopup,
  onKioskAddClicked,
  renderWeightVariant,
  renderPriceFavAction,
  renderContinueShopping,
  className,
  classNameCounter,
}) => {
  const [, setState] = useUiStore();
  const {
    addAndRemoveProductAttributes,
    measureSurfsideAction,
    measureAddToCartGA4,
  } = useAnalytics();
  const { productCard } = useData();
  const { products, update, add } = useProducts();
  const { notify } = useNotify();
  const [theProductIsAlreadyInTheCart, setTheProductIsAlreadyInTheCart] =
    useState(false);
  const [variantsSelectOptions, setVariantsSelectOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [index, setIndex] = useState(-1);
  const [quantity, setQuantity] = useState(0);
  const [weightVariant, setWeightVariant] = useState<ISelectOption<string>>();
  const { treez_store_name } = useEcomStoreSelector(['treez_store_name']);
  const [isSelectedStore, setIsSelectedStore] = useState(false);
  const [, setUiState] = useUiStore();
  const { unitPricingTier, setSelectedProductId } = useProductContext();
  const hasTierPricing = product?.pricing?.price_type === 'TIER';

  const siblingSelectOptions = useMemo(() => {
    const siblings = product.siblings ?? [];
    if (siblings.length <= 1) return [];
    return siblings
      .filter(sibling =>
        Boolean(sibling?.id && (sibling?.size || sibling?.name)),
      )
      .map(sibling => {
        const variant = sibling?.variants?.[0];
        const size = sibling?.size;
        const weight = formatWeightLabel(variant as Variant);
        const label = weight?.endsWith('G')
          ? `${weight.split('G')[0]} gram`
          : weight || size || '';
        return { label, value: sibling.id as string };
      })
      .filter(o => o.value);
  }, [product.siblings]);

  const hasSiblingSelect = siblingSelectOptions.length > 0;

  const updateSiblingInCart = useCallback(
    (siblingId: string) => {
      if (index === -1) return;
      const sibling = product.siblings?.find(s => s.id === siblingId);
      if (!sibling?.variants?.length) return;

      const sortedVariants = sortVariantByPrice(sibling.variants as Variant[]);
      if (!sortedVariants.length) return;
      const newWeightVariant = sortedVariants[0];
      const newProducts = cloneDeep(products);
      const lineItem = newProducts[index] as CartLineItem;

      const merged: CartLineItem = {
        ...lineItem,
        ...sibling,
        siblings: product.siblings,
        _id: lineItem._id,
        quantity: lineItem.quantity,
        itemLocId: getProductItemLocId(treez_store_name, newWeightVariant),
        weightVariant: newWeightVariant,
      };

      const tierMethod = merged.pricing?.tier_method;
      if (
        tierMethod === 'UNIT' &&
        merged.pricing?.tier_pricing_detail?.length
      ) {
        const tier0 = merged.pricing.tier_pricing_detail[0];
        merged.unitPricingTier = {
          qty: tier0.start_value,
          price: tier0.price_per_value,
        };
        merged.quantity = tier0.start_value;
      } else {
        delete merged.unitPricingTier;
        merged.quantity = lineItem.quantity;
      }

      newProducts[index] = merged;
      update(newProducts);
      setSelectedProductId(siblingId);
    },
    [
      index,
      product.siblings,
      products,
      treez_store_name,
      update,
      setSelectedProductId,
    ],
  );

  const cartLineBundleId =
    index >= 0 ? (products[index] as CartLineItem)?.bundle_id : undefined;

  const { bundleId, notifyBundleId, isDealPageAdd } =
    resolvePreCartSpecialContext(product, products, cartLineBundleId);

  // Deal used to drive the notify/progress toast. For an unambiguous add it is the
  // resolved bundleId; for a multi-deal SKU it falls back to the product's first
  // Pre-Cart deal. UX only — never persisted on the cart line.
  const notifyDealId = notifyBundleId;

  const {
    currentBundleProducts,
    dependentProducts,
    independentProducts,
    special,
  } = useBundleSpecial({
    id: notifyDealId ?? '',
  });

  useEffect(() => {
    setIsSelectedStore(!!treez_store_name);
  }, [treez_store_name]);

  useEffect(() => {
    // Match a cart line by SKU (id + weight variant). The Counter only needs to know
    // whether this SKU is already in the cart; per-deal progress is owned by the
    // route-scoped tracker (getCartLinesForSpecial), not by this lookup.
    const index = findIndex(
      products,
      (p: CartLineItem) =>
        p?._id === (product as CartLineItem)?._id ||
        (p.id == product.id &&
          p?.weightVariant?.id === selectedWeightVariant?.id),
    );

    const p = products?.[index];

    setTheProductIsAlreadyInTheCart(p !== undefined);

    setIndex(index);
    setQuantity(p?.quantity ?? 0);

    if (!p) {
      const defaultVariant = sortVariantByPrice(
        product?.variants as Variant[],
      )?.[0];

      setWeightVariant({
        label: formatWeightLabel(selectedWeightVariant ?? defaultVariant),
        value: selectedWeightVariant?.id ?? defaultVariant?.id ?? '',
      });
    }

    if (p !== undefined) {
      setWeightVariant({
        label: formatWeightLabel(p?.weightVariant),
        value: p?.weightVariant?.id ?? '',
      });
    }

    setVariantsSelectOptions(
      sortVariantByPrice(product?.variants as Variant[])?.map(v => {
        return { label: formatWeightLabel(v), value: v?.id ?? '' };
      }),
    );
  }, [products, product, selectedWeightVariant]);

  const addItemToTheCart = (
    event: MouseEvent<Element, globalThis.MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isSelectedStore) {
      setUiState({ is_store_location_open: true });
      return;
    }

    const selectVariant = product?.variants?.find(
      variant => variant?.id === weightVariant?.value,
    );

    const currentWeightVariant =
      selectVariant ?? sortVariantByPrice(product?.variants as Variant[])[0];

    const itemLocId = getProductItemLocId(
      treez_store_name,
      currentWeightVariant,
    );

    // save the product on the
    // persisted store
    let item: CartLineItem = {
      ...product,
      _id: `${product.id}-${Math.random()}`,
      itemLocId,
      quantity: unitPricingTier?.qty ?? 1,
      weightVariant: currentWeightVariant,
    };

    // if product does not have a bundle_id then we need to set the
    // bundle info on the product
    if (!(product as CartLineItem)?.bundle_id && bundleId) {
      item = {
        ...item,
        bundle_id: bundleId,
        bundle_title: special?.title,
        bundle_slug: special?.specialSlug,
      };
    }

    /**
     * Check if the product is of type GET (penny) and if the bundle has started, and in case it's complete,
     * we don't allow adding the GET product
     */

    const itemSalePrice = item?.weightVariant?.salePrice ?? Infinity;

    if (
      !canAddSpecialProductToCart({
        special,
        price: itemSalePrice,
        independentProductsInCart: independentProducts,
      })
    ) {
      notify('error', ERROR_ADD_PENNY_PRODUCT_MESSAGE);
      return;
    }

    let bogoOpenCart = false;

    if (notifyDealId && special) {
      const isBogo = special.method === SPECIAL_METHOD.BOGO;
      const isGetAdd =
        !!item?.id &&
        isBogoGetAdd(special, item.id, itemSalePrice, isDealPageAdd);

      if (isBogo) {
        const classifyParams = buildBogoClassifyParams(
          special,
          currentBundleProducts,
          products,
        );

        if (isDealPageAdd) {
          bogoOpenCart = runBogoDealPageAddNotify({
            notify,
            special,
            item,
            isGetAdd,
            classifyParams,
            productName: product?.name,
          });
        } else {
          notifyCatalogBogoAdd({
            notify,
            special,
            itemId: item?.id,
            itemSalePrice,
            independentProductsCount: independentProducts.length,
            productName: product?.name,
          });
        }
      } else {
        notifyNonBogoBundleAdd({
          notify,
          special,
          productName: product?.name,
          dependentProductsCount: dependentProducts.length,
          independentProductsCount: independentProducts.length,
        });
      }
    } else {
      notifyRegularProductAdd({ notify, productName: product?.name });
    }

    add(item);

    measureAddToCartGA4(item, 1);
    measureSurfsideAction([item], 'add');

    const shouldOpenCart = resolveShouldOpenCartOnAdd({
      bundleId,
      special,
      bogoOpenCart,
    });

    shouldOpenCart && setState({ is_cart_open: true });
  };

  const updateWeightVariant = (v: ISelectOption<string>) => {
    const newWeightVariant = find(
      product.variants,
      p => p?.id == v.value,
    ) as Variant;

    // save the product on the
    // persisted store
    const newProducts = cloneDeep(products);
    newProducts[index].weightVariant = newWeightVariant;
    newProducts[index].itemLocId = getProductItemLocId(
      treez_store_name,
      newWeightVariant,
    );

    // Disable it for now
    // if (calculateTotalGrams(newProducts) > gramsLimit) {
    //   notify('error', GRAM_LIMIT_EXCEEDED_ERROR);
    //   return;
    // }

    setWeightVariant({
      label: formatWeightLabel(newWeightVariant),
      value: newWeightVariant?.id ?? '',
    });
    update(newProducts);
  };

  const buttonText = productCard?.checkAvilibility || 'Choose your Store';

  const addToCart = (
    <Button
      ariaLabel={
        isSelectedStore || omitCheckAvilibility
          ? `Add ${product?.name || 'product'} to cart`
          : buttonText
      }
      className={classNames(
        style['counter__btn-responsibe'],
        { [style['counter__btn-update-cart']]: onDetail },
        { [style['counter__btn-check']]: !isSelectedStore },
      )}
      color={`${actionTheme}`}
      data-testid={CounterTestIds.ADD_TO_CART_BUTTON}
      onClick={e => {
        addItemToTheCart(e);
        onKioskAddClicked?.();
      }}
      {...(addAndRemoveProductAttributes(
        'addToCart',
        product as CartLineItem,
      ) as any)}
    >
      {isSelectedStore || omitCheckAvilibility ? (
        <>
          {showIcon && (
            <figure className={style['counter__btn-icon']}>
              <IconSvg name="shopping" />
            </figure>
          )}
          <span className={style.counter__text}>Add To Cart</span>
        </>
      ) : (
        <span className={style.counter__text}>{buttonText}</span>
      )}
    </Button>
  );

  const getUIElements = () => {
    // This case is for when it is a complete
    // new product
    if (!theProductIsAlreadyInTheCart && !onDetail) {
      return addToCart;
    }

    // This case is for when it is a complete
    // new product in the Detail page
    if (onDetail) {
      return (
        <div className={style?.counter__product_detail}>
          {renderWeightVariant}
          <div className={style.counter__product_detail_actions}>
            {renderPriceFavAction}
            {!theProductIsAlreadyInTheCart && addToCart}
            {theProductIsAlreadyInTheCart && (
              <div
                className={classNames(style.counter__container, {
                  [style.counter__container_shopping]: onShopping,
                  [style.counter__container_detail]: onDetail,
                })}
              >
                <CounterActions
                  index={index}
                  onKiosk={onKiosk}
                  product={product as CartLineItem}
                  quantity={quantity}
                  weightVariant={weightVariant?.value}
                />
              </div>
            )}
          </div>
          {renderContinueShopping}
        </div>
      );
    }

    // This case is for when the product is already on the
    // shipping cart but the counter component is beeing
    // display on the Product Card component
    if (theProductIsAlreadyInTheCart && onCard) {
      return (
        <CounterActions
          className={classNameCounter}
          index={index}
          onKiosk={onKiosk}
          product={product as CartLineItem}
          quantity={quantity}
          weightVariant={weightVariant?.value}
        />
      );
    }

    // The default value is for product datail and
    // shipping cart
    return (
      <form
        className={classNames(style.counter__container, {
          [style.counter__container_shopping]: onShopping,
          [style.counter__container_detail]: onDetail,
        })}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <CounterActions
          index={index}
          onKiosk={onKiosk}
          product={product as CartLineItem}
          quantity={quantity}
          weightVariant={weightVariant?.value}
        />
        <div>
          {hasSiblingSelect && (
            <CounterProductSiblings
              onDetail={onDetail}
              onShopping={onShopping}
              onSiblingChange={updateSiblingInCart}
              options={siblingSelectOptions}
            />
          )}
          {!hasSiblingSelect &&
            hasTierPricing &&
            (product.pricing?.tier_method === 'UNIT' ? (
              <ProductUnitPricingTiers
                onDetail={onDetail}
                onShopping={onShopping}
              />
            ) : (
              variantsSelectOptions.length > 0 && (
                <Select
                  className={classNames(style['counter__variant-select'], {
                    [style.counter__variant_select_shopping]: onShopping,
                    [style.counter__variant_select_detail]: onDetail,
                  })}
                  defaultValue={weightVariant}
                  menuClassName={style['variant-select__menu']}
                  name="weight_variant"
                  onChange={updateWeightVariant}
                  options={variantsSelectOptions}
                  showRemainingCount
                  value={weightVariant}
                />
              )
            ))}
          {/* the product has price type "FLAT" */}
          {!hasSiblingSelect && !hasTierPricing && weightVariant?.label && (
            <div className={style?.counter__flat_price_label}>
              <span>{weightVariant.label}</span>
            </div>
          )}
        </div>
      </form>
    );
  };

  // this is to hide the add to cart button when
  // the product is out of stock
  if (!onDetail) {
    if (product?.isLowStock) {
      return (
        <span className={classNames(style.counter__warning, className)}>
          In Store Only
        </span>
      );
    }
    if (product?.isOutStock) {
      return (
        <span className={classNames(style.counter__warning, className)}>
          Out of Stock
        </span>
      );
    }
  }

  return (
    <div className={classNames(style.counter, className)}>
      {getUIElements()}
    </div>
  );
};

const CounterProductSiblings: FC<{
  options: ISelectOption<string>[];
  onSiblingChange: (_siblingId: string) => void;
  onShopping?: boolean;
  onDetail?: boolean;
}> = ({ options, onSiblingChange, onShopping, onDetail }) => {
  const { selectedProductId, product: ctxProduct } = useProductContext();

  const selectedOption = useMemo(
    () =>
      options.find(o => o.value === (selectedProductId ?? ctxProduct.id)) ??
      options[0],
    [options, selectedProductId, ctxProduct.id],
  );

  const selectAriaLabel = selectedOption?.label
    ? `Select product size. Current selection: ${selectedOption.label}${
        options.length > 1
          ? `. ${options.length - 1} more options available`
          : ''
      }`
    : 'Select product size';

  return (
    <Select
      ariaLabel={selectAriaLabel}
      className={classNames(style['counter__variant-select'], {
        [style.counter__variant_select_shopping]: onShopping,
        [style.counter__variant_select_detail]: onDetail,
      })}
      defaultValue={selectedOption}
      menuClassName={style['variant-select__menu']}
      name="product_sibling"
      onChange={option => onSiblingChange(option.value)}
      options={options}
      placement="bottom-start"
      showRemainingCount
      value={selectedOption}
    />
  );
};

const ProductUnitPricingTiers: FC<{
  onShopping?: boolean;
  onDetail?: boolean;
}> = ({ onDetail, onShopping }) => {
  const {
    product,
    unitPricingTiers,
    unitPricingTier,
    updateUnitPricingTier,
    quantity,
  } = useProductContext();

  const unitPricingTiersOptions = useMemo(
    () =>
      unitPricingTiers?.map(({ qty, price }) => ({
        label: `${qty} for $${currencyFormat(price * qty * 100)}`,
        value: { qty, price },
      })) ?? [],
    [product],
  );

  const selectedTier = useMemo(() => {
    return find(
      unitPricingTiersOptions,
      option =>
        option.value.qty === unitPricingTier?.qty &&
        option.value.price === unitPricingTier?.price,
    );
  }, [unitPricingTiersOptions, unitPricingTier]);

  const selectedLabel = selectedTier
    ? `$${currencyFormat(selectedTier.value.price! * quantity! * 100)}`
    : '';

  return (
    <Select
      ariaLabel={
        selectedLabel
          ? `Select unit pricing tier. Current selection: ${
              selectedTier?.label || selectedLabel
            }`
          : 'Select unit pricing tier'
      }
      className={classNames(style['counter__variant-select'], {
        [style.counter__variant_select_shopping]: onShopping,
        [style.counter__variant_select_detail]: onDetail,
      })}
      getSelectedLabel={option =>
        `$${currencyFormat(option?.value?.price! * quantity! * 100)}`
      }
      menuClassName={style['variant-select__menu']}
      onChange={({ value }) => updateUnitPricingTier(value)}
      options={unitPricingTiersOptions}
      placement="bottom-start"
      value={selectedTier}
    />
  );
};

export default Counter;
